import { create } from 'zustand';
import { getTenantInvoices } from '../api/generated/tenant-invoices/tenant-invoices';
import { useToastStore } from './useToastStore';
import { showApiError } from '../errors';
import { downloadTenantInvoicePdf } from '@/utils/downloadReport';
import i18n from '@/i18n/config';
import type {
  GenerateMonthlyTenantInvoicesResponse,
  TenantInvoiceDto,
  TenantInvoiceStatus,
} from '../api/types/admin';

const {
  getApiV1TenantBillingInvoices,
  getApiV1TenantBillingInvoicesId,
  postApiV1TenantBillingInvoicesGenerateMonthly,
  patchApiV1TenantBillingInvoicesIdMarkPaid,
  patchApiV1TenantBillingInvoicesIdCancel,
} = getTenantInvoices();

const t = (key: string) => i18n.t(key);

export interface TenantInvoiceFilters {
  tenantId?: string;
  status?: TenantInvoiceStatus;
  year?: number;
}

interface TenantInvoiceState {
  invoices: TenantInvoiceDto[];
  filters: TenantInvoiceFilters;
  loading: boolean;
  saving: boolean;
  downloadingPdf: boolean;
  error: string | null;

  setFilters: (f: Partial<TenantInvoiceFilters>) => void;
  fetchAll: () => Promise<void>;
  fetchForTenant: (tenantId: string) => Promise<TenantInvoiceDto[]>;
  getById: (id: string) => Promise<TenantInvoiceDto | null>;
  downloadPdf: (id: string, invoiceNumber?: string | null) => Promise<void>;
  generateMonthly: (
    year: number,
    month: number,
  ) => Promise<GenerateMonthlyTenantInvoicesResponse | null>;
  markPaid: (id: string) => Promise<boolean>;
  cancel: (id: string) => Promise<boolean>;
}

export const useTenantInvoiceStore = create<TenantInvoiceState>((set, get) => ({
  invoices: [],
  filters: {},
  loading: false,
  saving: false,
  downloadingPdf: false,
  error: null,

  setFilters: (f) => {
    set((s) => ({ filters: { ...s.filters, ...f } }));
  },

  fetchAll: async () => {
    set({ loading: true, error: null });
    try {
      const { tenantId, status } = get().filters;
      const response = await getApiV1TenantBillingInvoices({
        tenantId: tenantId || undefined,
        status,
      });
      const sorted = [...(response ?? [])].sort((a, b) => {
        const da = a.periodStart ? new Date(a.periodStart).getTime() : 0;
        const db = b.periodStart ? new Date(b.periodStart).getTime() : 0;
        return db - da;
      });
      set({ invoices: sorted, loading: false });
    } catch (error) {
      const apiError = showApiError(error);
      set({ error: apiError.code, loading: false });
    }
  },

  fetchForTenant: async (tenantId) => {
    try {
      const response = await getApiV1TenantBillingInvoices({ tenantId });
      return response ?? [];
    } catch (error) {
      showApiError(error);
      return [];
    }
  },

  getById: async (id) => {
    try {
      return await getApiV1TenantBillingInvoicesId(id);
    } catch (error) {
      showApiError(error);
      return null;
    }
  },

  downloadPdf: async (id, invoiceNumber) => {
    set({ downloadingPdf: true });
    try {
      await downloadTenantInvoicePdf(id, invoiceNumber);
      useToastStore.getState().addToast(t('admin:tenantInvoices.messages.pdfDownloaded'), 'success');
    } catch (error) {
      showApiError(error);
    } finally {
      set({ downloadingPdf: false });
    }
  },

  generateMonthly: async (year, month) => {
    set({ saving: true });
    try {
      const result = await postApiV1TenantBillingInvoicesGenerateMonthly({ year, month });
      set({ saving: false });
      await get().fetchAll();
      const generated = result?.generated ?? 0;
      const skipped = result?.skipped ?? 0;
      useToastStore.getState().addToast(
        t('admin:tenantInvoices.generateModal.' + (generated === 1 ? 'success_one' : 'success_other'))
          .replace('{{count}}', String(generated))
          .replace('{{skipped}}', String(skipped)),
        'success',
      );
      return result;
    } catch (error) {
      showApiError(error);
      set({ saving: false });
      return null;
    }
  },

  markPaid: async (id) => {
    set({ saving: true });
    try {
      const updated = await patchApiV1TenantBillingInvoicesIdMarkPaid(id);
      set((s) => ({
        invoices: s.invoices.map((inv) => (inv.id === id ? { ...inv, ...updated } : inv)),
        saving: false,
      }));
      useToastStore.getState().addToast(t('admin:tenantInvoices.messages.markedPaid'), 'success');
      return true;
    } catch (error) {
      showApiError(error);
      set({ saving: false });
      return false;
    }
  },

  cancel: async (id) => {
    set({ saving: true });
    try {
      const updated = await patchApiV1TenantBillingInvoicesIdCancel(id);
      set((s) => ({
        invoices: s.invoices.map((inv) => (inv.id === id ? { ...inv, ...updated } : inv)),
        saving: false,
      }));
      useToastStore.getState().addToast(t('admin:tenantInvoices.messages.cancelled'), 'success');
      return true;
    } catch (error) {
      showApiError(error);
      set({ saving: false });
      return false;
    }
  },
}));
